import { useLocationService } from '@/services/location/LocationService';
import { RealtimeEvents, useRealtimeEvent, useRealtimeLocation } from '@/services/realtime/RosteringRealtimeProvider';
import { useCallback } from 'react';
import { OptimiserJob, OptimiserStatus } from './optimiser.types';

interface BatchJobStateChangedEvent {
  id: string;
  batchId: string;
  started?: string;
  finished?: string;
  properties: string;
}

const MULTI_DAY_TEMPLATED_TYPE = 'OptimiseMultiDayTemplatedStatus';

const toOptimiserJob = (change: BatchJobStateChangedEvent): OptimiserJob | undefined => {
  let raw: any;

  try {
    raw = JSON.parse(change.properties);
  } catch (error) {
    console.warn('Failed to parse optimiser SignalR properties.', { properties: change.properties, error });
    return undefined;
  }

  const type = raw.type ?? raw.Type;
  if (type !== MULTI_DAY_TEMPLATED_TYPE) {
    return undefined;
  }

  const locationId = raw.locationId ?? raw.LocationId;
  const from = raw.from ?? raw.From;
  const to = raw.to ?? raw.To;
  const rawStatus = raw.status ?? raw.Status;

  if (!locationId || !from || !to || rawStatus === undefined || rawStatus === null) {
    console.warn('Invalid optimiser SignalR properties.', {
      raw,
      locationId,
      from,
      to,
      rawStatus
    });
    return undefined;
  }

  const status = String(rawStatus) as OptimiserStatus;

  const complete = !!change.finished || status === OptimiserStatus.FINISHED || status === OptimiserStatus.ERROR;

  const effectiveStatus = complete && status !== OptimiserStatus.ERROR ? OptimiserStatus.FINISHED : status;

  return {
    batchId: change.batchId,
    locationIds: [locationId],
    startDate: new Date(from),
    endDate: new Date(to),
    started: change.started ? new Date(change.started) : undefined,
    complete,
    status: effectiveStatus
  };
};

// Receives optimiser progress over the shared realtime connection (see RosteringRealtimeProvider).
export const useOptimiserBatchSignalR = (upsertJob: (job: OptimiserJob) => void) => {
  const selectedLocationId = useLocationService(state => state.selectedLocation?.id);

  useRealtimeLocation(selectedLocationId);

  const onChange = useCallback(
    (change: BatchJobStateChangedEvent) => {
      const job = toOptimiserJob(change);
      // The shared connection receives every location any tab shows; keep this tab's own.
      if (
        job &&
        selectedLocationId !== undefined &&
        job.locationIds.some(id => Number(id) === Number(selectedLocationId))
      ) {
        upsertJob(job);
      }
    },
    [upsertJob, selectedLocationId]
  );

  useRealtimeEvent(RealtimeEvents.BatchJobStateChanged, onChange);
};
